<template>
  <button
    ref="button"
    :type="type"
    class="base-button"
    :class="{
      [variant]: !!variant,
      [size]: !!size,
      inverted,
      icon,
      loading,
      success,
    }"
    :disabled="disabled || loading || success"
    :data-qa="dataQa"
    @click="handleClick"
  >
    <div
      v-if="hasSlot['prepend'] && !icon && !loading && !success"
      class="base-button__prepend"
    >
      <slot name="prepend" />
    </div>
    <span class="center">
      <slot>
        {{ text }}
      </slot>
    </span>
    <CheckIcon
      v-if="success"
      class="success-icon"
    />
    <div
      v-if="hasSlot['append'] && !icon && !loading && !success"
      class="base-button__append"
    >
      <slot name="append" />
    </div>
  </button>
</template>

<script setup>
import { ref } from 'vue'
import useVueSlots from '@shared/composables/useVueSlots.js'
import CheckIcon from '@shared/assets/icon/CheckIcon.vue'

const { hasSlot } = useVueSlots(['prepend', 'append', 'default'])

const button = ref()
const emit = defineEmits(['click'])

const props = defineProps({
  type: {
    type: String,
    default: 'button',
  },
  icon: {
    type: Boolean,
    default: false,
  },
  text: {
    type: String,
    default: '',
  },
  // INFO: Inverted styles are not yet implemented for all variants.
  inverted: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'primary',
    validator: (val) =>
      ['primary', 'emphasized', 'destructive', 'outlined', 'ghost'].includes(
        val,
      ),
  },
  size: {
    type: String,
    default: 'regular',
    validator: (val) => ['regular', 'small'].includes(val),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  success: {
    type: Boolean,
    default: false,
  },
  dataQa: {
    type: String,
    default: '',
  },
})

const handleClick = (e) => {
  e.preventDefault()
  if (props.loading || props.disabled || props.success) return

  emit('click', e)
}

defineExpose({ button })
</script>

<style lang="scss">
@use 'sass:map';

$button-background: (
  interactive-accent: $kds-color-background-interactive-accent,
  interactive-accent-hover: $kds-color-background-interactive-accent-hover,
  interactive-accent-active: $kds-color-background-interactive-accent-active,

  interactive-destructive: $kds-color-background-interactive-destructive,
  interactive-destructive-hover:
    $kds-color-background-interactive-destructive-hover,
  interactive-destructive-active:
    $kds-color-background-interactive-destructive-active,

  interactive-disabled-inverse:
    $kds-color-background-interactive-disabled-inverse,

  // INFO: This is #071D3F1A in the designs but when using it within the loading animation it won't work. So I used a color picker to get the 6 character hex code.
  interactive-disabled: #e6e8ec,

  interactive-strong: $kds-color-background-interactive-strong,
  interactive-strong-hover: $kds-color-background-interactive-strong-hover,
  interactive-strong-active: $kds-color-background-interactive-strong-active,

  interactive-strong-inverse: $kds-color-background-interactive-strong-inverse,
  interactive-strong-inverse-hover:
    $kds-color-background-interactive-strong-hover-inverse,
  interactive-strong-inverse-active:
    $kds-color-background-interactive-strong-active-inverse,

  interactive-subtle: $kds-color-background-interactive-subtle,
  interactive-subtle-hover: $kds-color-background-interactive-subtle-hover,
  interactive-subtle-active: $kds-color-background-interactive-subtle-active,

  interactive-subtle-inverse: $kds-color-background-interactive-subtle-inverse,
  interactive-subtle-inverse-hover:
    $kds-color-background-interactive-subtle-hover-inverse,
);

$button-text: (
  on-interactive-destructive: $kds-color-text-on-interactive-destructive,
  on-interactive-disabled: $kds-color-text-on-interactive-disabled,
  on-interactive-subtle: $kds-color-text-on-interactive-subtle,
);

$button-border-color: (
  on-interactive: $kds-color-line-on-interactive,
  on-interactive-inverse: $kds-color-line-on-interactive-inverse,
  on-interactive-focus: $kds-color-line-focus,
);

:root {
  // TODO
  --base-button--padding: 16px 32px;
  --base-button--border-radius: 100px;
  --base-button--border-style: none;
  --base-button--border-width: 0;
  --base-button--border-color: 0;
  --base-button--outline-width: 1.5px;
  --base-button--height: auto;
  --base-button--opacity: 1;
  --base-button--background-color: #{map.get(
      $button-background,
      interactive-strong
    )};
  --base-button--color: #{$kds-color-text-primary-inverse};
  --base-button--cursor: pointer;
  --base-button--visibility: visible;
  --base-button--loading-background-color-before: conic-gradient(
    transparent,
    #{map.get($button-background, interactive-strong)}
  );
  --base-button--loading-background-color-after: #{map.get(
      $button-background,
      interactive-strong
    )};
}

/*
  INFO: Mixin for button text color based on variant
*/
@mixin button-text($variant) {
  --base-button--color: #{map.get($button-text, 'on-interactive-#{$variant}')};
}

/*
  INFO: Mixin for button background and text color based on variant
    Also includes hover and active states
*/
@mixin button-styles($variant) {
  --base-button--background-color: #{map.get(
      $button-background,
      'interactive-#{$variant}'
    )};

  &:hover {
    --base-button--background-color: #{map.get(
        $button-background,
        'interactive-#{$variant}-hover'
      )};
  }

  &:focus {
    --base-button--background-color: #{map.get(
        $button-background,
        'interactive-#{$variant}-hover'
      )};
  }

  &:active {
    --base-button--background-color: #{map.get(
        $button-background,
        'interactive-#{$variant}-active'
      )};
  }
}

.base-button {
  @include flex-container(row, center, sm, center);
  @include kds-typography-interactive-medium();
  position: relative;
  width: 100%;
  background-color: var(--base-button--background-color);
  color: var(--base-button--color);
  border-radius: var(--base-button--border-radius);
  border-width: var(--base-button--border-width);
  border-color: var(--base-button--border-color);
  border-style: var(--base-button--border-style);
  padding: var(--base-button--padding);
  height: var(--base-button--height);
  cursor: var(--base-button--cursor);
  opacity: var(--base-button--opacity);
  // Add scale transition for iOS-like press feedback
  transition:
    background 0.25s ease,
    transform 0.1s ease-out;
  transform-origin: center center;
  will-change: transform;
  -webkit-tap-highlight-color: transparent;

  .success-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  > div {
    @include flex-container(row);
  }

  .center {
    visibility: var(--base-button--visibility);
  }

  &.small {
    --base-button--padding: 8px 16px;
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }

  &:focus-visible {
    --base-button--border-color: #{map.get(
        $button-border-color,
        on-interactive-focus
      )};
    outline-color: #{map.get($button-border-color, on-interactive-focus)};
    outline-style: solid;
    outline-width: var(--base-button--outline-width);

    -webkit-box-shadow: inset 0 0 0 1.5px
      #{map.get($button-border-color, on-interactive-focus)};
    -moz-box-shadow: inset 0 0 0 1.5px
      #{map.get($button-border-color, on-interactive-focus)};
    box-shadow: inset 0 0 0 1.5px
      #{map.get($button-border-color, on-interactive-focus)};
  }

  &.icon {
    width: 54px;
    --base-button--height: 54px;
    --base-button--border-radius: 50%;
    --base-button--padding: 16px;

    .center {
      @include flex-container(column, center);
    }

    &.small {
      width: 34px;
      --base-button--height: 34px;
    }
  }

  &:disabled {
    --base-button--opacity: 0.9;
    --base-button--background-color: #{map.get(
        $button-background,
        interactive-disabled
      )};

    --base-button--color: #{map.get($button-text, on-interactive-disabled)};
    --base-button--cursor: not-allowed;

    &.outlined {
      --base-button--border-style: solid;
      --base-button--border-width: 1.5px;
      --base-button--border-color: #{$kds-color-background-interactive-subtle-active};
      --base-button--background-color: #{$kds-color-background-base};
      --base-button--loading-background-color-after: #{$kds-color-background-base};
    }

    &.loading {
      --base-button--cursor: not-allowed;
      --base-button--background-color: #{map.get(
          $button-background,
          interactive-disabled
        )};

      --base-button--loading-background-color-after: #{map.get(
          $button-background,
          interactive-disabled
        )};

      &.outlined {
        --base-button--border-style: solid;
        --base-button--border-width: 1.5px;
        --base-button--border-color: #{$kds-color-background-interactive-subtle-active};
        --base-button--background-color: #{$kds-color-background-base};
        --base-button--loading-background-color-after: #{$kds-color-background-base};
      }
    }
  }

  &.success {
    --base-button--color: #{$kds-color-text-primary};
    position: relative;
    --base-button--visibility: hidden;
  }

  &.loading {
    --base-button--visibility: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: var(--base-button--loading-background-color-before);
      animation: spin 1s linear infinite;
    }

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 17px;
      height: 17px;
      border-radius: 50%;
      transform: translate(-50%, -50%);
      background-color: var(--base-button--loading-background-color-after);
    }
  }

  &:not([disabled]) {
    &.primary {
      @include button-styles(strong);

      &.inverted {
        @include button-text(subtle);
        @include button-styles(strong-inverse);
      }
    }

    &.emphasized {
      @include button-styles(accent);
    }

    &.destructive {
      @include button-styles(destructive);
    }

    &.outlined {
      --base-button--background-color: 0;
      --base-button--border-style: solid;
      --base-button--border-width: 1.5px;

      --base-button--border-color: #{$kds-color-background-interactive-subtle-active};

      @include button-text(subtle);
      @include button-styles(subtle);

      &.inverted {
        @include button-text(subtle);
        @include button-styles(subtle-inverse);
      }

      &:focus-visible {
        --base-button--outline-width: 0;
        --base-button--border-color: #{map.get(
            $button-border-color,
            on-interactive-focus
          )};
      }
    }

    &:active {
      transform: scale(0.98);
    }

    &:focus-visible:active {
      transform: scale(0.98);
    }

    &.ghost {
      @include button-text(subtle);
      @include button-styles(subtle);

      &.inverted {
        @include button-text(subtle);
        @include button-styles(subtle-inverse);
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .base-button {
    transition: background 0.25s ease;
  }
  .base-button:not([disabled]):active,
  .base-button:not([disabled]):focus-visible:active {
    transform: none;
  }
}

@keyframes spin {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
</style>
